import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';
import { useLoginTheme } from '@/contexts/LoginThemeContext';
import { TeamModel } from '@/database/models/team';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export const LoginPage: React.FC = () => {
  const { signIn, teamMember } = useAuth();
  const { setUserSiteOfficeAccess } = useSiteOffice();
  const { currentTheme } = useLoginTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('rememberedEmail') || '';
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isNewMemberMode, setIsNewMemberMode] = useState(false);
  const [memberInfo, setMemberInfo] = useState<any>(null);
  const [showSetupOption, setShowSetupOption] = useState(false);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('rememberMe') === 'true';
  });

  // Check if email is administrator email to show setup option
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Show setup option only if email is administrator email
    setShowSetupOption(newEmail.toLowerCase() === 'dvdhabib@gmail.com');
  };

  const handleSetupMode = () => {
    localStorage.setItem('setupMode', 'true');
    window.location.reload();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsEmailConfirmation(false);
    setLoading(true);

    try {
      if (isNewMemberMode) {
        // Handle password creation
        if (!password) {
          throw new Error('Please enter a password');
        }

        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }

        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        await TeamModel.setMemberPassword(email.trim(), password);
        
        toast({
          title: 'Password created successfully!',
          description: 'Your password has been set. Please wait for administrator approval to access the application.',
        });
        
        // Reset to login mode and clear member info
        setIsNewMemberMode(false);
        setPassword('');
        setConfirmPassword('');
        setMemberInfo(null);
        setEmail('');
        
      } else {
        // Handle normal login
        if (!email || !password) {
          throw new Error('Please enter both email and password');
        }

        // Resolve identifier: if user typed a name (no '@'), convert to internal email
        let loginEmail = email.trim();
        if (!loginEmail.includes('@')) {
          try {
            const { data: resolved, error: resolveErr } = await supabase.functions.invoke(
              'resolve-login-identifier',
              { body: { identifier: loginEmail } }
            );
            if (resolveErr || !resolved?.email) {
              throw new Error(resolved?.error || 'Nama tidak ditemukan. Pastikan ejaan benar.');
            }
            loginEmail = resolved.email;
          } catch (e) {
            throw new Error(e instanceof Error ? e.message : 'Gagal memverifikasi nama');
          }
        }

        // Normal login process - Supabase Auth handles all password management
        const result = await signIn(loginEmail, password);
        if (!result.success) {
          if (result.needsEmailConfirmation) {
            setNeedsEmailConfirmation(true);
          }
          throw new Error(result.error || 'Login failed');
        }

        // Save or clear remembered email
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email.trim());
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Set user's site office access after successful login
        // Get member data to check their site office assignment
        const loggedInMember = await TeamModel.findTeamMemberByEmail(loginEmail);
        if (loggedInMember) {
          // Administrators get access to all site offices regardless of assignment
          const isAdmin = loggedInMember.role === 'administrator' || loggedInMember.is_admin;
          const accessId = isAdmin ? null : (loggedInMember.siteOfficeId || null);
          console.log('🏢 Setting user site office access:', accessId, '(isAdmin:', isAdmin, ')');
          setUserSiteOfficeAccess(accessId);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    setResendingConfirmation(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: cleanEmail,
      });

      if (resendError) {
        toast({
          title: 'Gagal mengirim email verifikasi',
          description: resendError.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Email verifikasi terkirim',
        description: 'Silakan cek inbox/spam dan klik link verifikasi, lalu coba login lagi.',
      });
    } catch (err) {
      toast({
        title: 'Gagal mengirim email verifikasi',
        description: err instanceof Error ? err.message : 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Masukkan email Anda terlebih dahulu');
      return;
    }

    setSendingReset(true);
    setError('');
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
        redirectTo: `${window.location.origin}/login`,
      });

      if (resetError) throw resetError;

      setResetSent(true);
      toast({
        title: 'Email reset password terkirim!',
        description: 'Silakan cek inbox atau folder spam Anda untuk link reset password.',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal mengirim email reset password');
    } finally {
      setSendingReset(false);
    }
  };

  const signInTitle = currentTheme.signInTitleText?.trim() || 'Selamat Datang';
  const signInSubtitle = currentTheme.signInSubtitleText?.trim() || 'Masuk untuk mengelola proyek Anda.';
  const leftTitle = currentTheme.signInLeftTitleText?.trim() || 'Project Tracking System';
  const leftDescription = currentTheme.signInLeftDescriptionText?.trim() || 'Solusi terpadu untuk monitoring dan manajemen progres konstruksi secara real-time dan transparan.';
  const bullet1 = currentTheme.signInBullet1?.trim() || 'Pantau progress proyek real-time';
  const bullet2 = currentTheme.signInBullet2?.trim() || 'Kelola dokumen dan anggaran';
  const bullet3 = currentTheme.signInBullet3?.trim() || 'Kolaborasi tim per site office';
  const logoUrl = currentTheme.signInLogoUrl?.trim() || '/logo-gcw.png';
  const leftImageUrl = currentTheme.signInLeftImageUrl?.trim() || '';
  const leftImageOpacity = Math.min(100, Math.max(0, currentTheme.signInLeftImageOpacity ?? 18)) / 100;

  const featureBullets = [bullet1, bullet2, bullet3];

  return (
    <div
      className="fixed inset-0 w-full flex bg-[#f8fafc] overflow-hidden"
      style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
    >
      {/* Left Branding Panel (desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#062a4a]">
        {/* Dot pattern + ambient glows */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #fff 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="absolute -top-[10%] -right-[10%] w-[500px] h-[500px] rounded-full bg-[#0ea5e9]/30 blur-3xl" />
          <div className="absolute -bottom-[10%] -left-[10%] w-[500px] h-[500px] rounded-full bg-[#0c2f52]/80 blur-3xl" />
        </div>

        {/* Gambar transparan (dapat diganti di menu Setting) */}
        {leftImageUrl && (
          <div className="absolute inset-0 pointer-events-none z-[1] flex items-center justify-center">
            <img
              src={leftImageUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover mix-blend-luminosity"
              style={{ opacity: leftImageOpacity }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#062a4a] via-[#062a4a]/70 to-[#062a4a]/30" />
          </div>
        )}

        <div className="relative z-10 w-full flex flex-col justify-between p-12 xl:p-16 text-white animate-fade-in">
          <div>
            <div className="flex items-center gap-3 mb-10">
              <div className="relative group">
                <div className="absolute -inset-6 rounded-full bg-[#0ea5e9]/25 blur-2xl animate-pulse" />
                <img
                  src={logoUrl}
                  alt="GCW Project - PT. Gemilang Cipta Wawasan"
                  className="relative h-20 xl:h-24 w-auto object-contain drop-shadow-[0_8px_24px_rgba(14,165,233,0.45)] transition-transform duration-500 ease-out group-hover:scale-105"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>


            <div className="max-w-md">
              <h1
                className="text-5xl font-extrabold tracking-tight leading-tight mb-6"
                style={{ fontFamily: "'Sora', system-ui, sans-serif" }}
              >
                {leftTitle} <span className="text-[#0ea5e9]">.</span>
              </h1>
              <p className="text-[#e0f2fe]/70 text-lg leading-relaxed mb-10">
                {leftDescription}
              </p>

              <ul className="space-y-6">
                {featureBullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-[#0ea5e9]/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#38bdf8]" strokeWidth={3} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#e0f2fe]">{bullet}</h3>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-[#e0f2fe]/40 text-sm">
            &copy; {new Date().getFullYear()} PT. Gemilang Cipta Wawasan. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Login Panel */}
      <div className="relative w-full lg:w-1/2 flex items-center justify-center p-5 sm:p-8 bg-white lg:bg-[#f8fafc] overflow-y-auto">
        {/* Gambar transparan versi mobile */}
        {leftImageUrl && (
          <div className="absolute inset-0 lg:hidden pointer-events-none">
            <img
              src={leftImageUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ opacity: leftImageOpacity * 0.8 }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/90 to-white" />
          </div>
        )}
        <div className="relative w-full max-w-[440px] animate-scale-in">
          <Card className="bg-white/95 backdrop-blur-sm p-2 sm:p-4 rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100">
            <CardHeader className="space-y-3 pb-2">
              {/* Logo on mobile only (desktop shows it on the left) */}
              <div className="flex justify-center lg:hidden">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-full bg-[#0ea5e9]/15 blur-xl animate-pulse" />
                  <img
                    src={logoUrl}
                    alt="PT. Gemilang Cipta Wawasan"
                    className="relative h-16 sm:h-20 w-auto object-contain drop-shadow-[0_6px_18px_rgba(2,132,199,0.25)] transition-transform duration-500 hover:scale-105"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="text-center lg:text-left">
                <h2
                  className="text-2xl sm:text-3xl font-bold text-slate-900"
                  style={{ fontFamily: "'Sora', system-ui, sans-serif" }}
                >
                  {signInTitle}
                </h2>
                <p className="text-sm text-slate-500 mt-2">{signInSubtitle}</p>
              </div>
            </CardHeader>

        <CardContent className="space-y-6">
          {forgotPasswordMode ? (
            /* Forgot Password Mode */
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-base font-semibold text-foreground">Lupa Password?</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Masukkan email Anda untuk menerima link reset password
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="Masukkan email Anda"
                  value={email}
                  onChange={handleEmailChange}
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {resetSent && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  <p className="font-medium">✅ Email reset password terkirim!</p>
                  <p className="mt-1 text-green-700">Cek inbox atau folder spam Anda.</p>
                </div>
              )}

              <Button
                type="button"
                className="w-full"
                onClick={handleForgotPassword}
                disabled={sendingReset}
              >
                {sendingReset && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Kirim Link Reset Password
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setForgotPasswordMode(false);
                  setError('');
                  setResetSent(false);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Kembali ke Sign In
              </Button>
            </div>
          ) : (
            /* Normal Login Mode */
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email atau Nama</Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder="Email atau nama (untuk member khusus)"
                    value={email}
                    onChange={handleEmailChange}
                    required
                    disabled={isNewMemberMode}
                  />
                </div>

                {!isNewMemberMode ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button
                        type="button"
                        onClick={() => {
                          setForgotPasswordMode(true);
                          setError('');
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        Lupa Password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="rememberMe"
                        checked={rememberMe}
                        onCheckedChange={(checked) => {
                          const val = checked === true;
                          setRememberMe(val);
                          localStorage.setItem('rememberMe', val ? 'true' : 'false');
                        }}
                      />
                      <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
                        Remember me
                      </Label>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-xl">👋</div>
                        <div>
                          <h3 className="font-semibold text-primary text-sm">
                            Welcome! Hi {memberInfo?.name}
                          </h3>
                          <p className="text-primary/80 text-xs mt-1">
                            Please create your password to complete account setup.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-password">Create Password</Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Error Display */}
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {needsEmailConfirmation && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleResendConfirmation}
                    disabled={resendingConfirmation || loading}
                  >
                    {resendingConfirmation && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Kirim Ulang Email Verifikasi
                  </Button>
                )}

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full py-6 rounded-lg font-semibold bg-[#0284c7] hover:bg-[#0369a1] text-white shadow-lg shadow-sky-500/30 active:scale-[0.98] transition-all"
                    disabled={loading}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isNewMemberMode ? 'Create Password' : 'Sign In'}
                  </Button>
                  
                  {showSetupOption && (
                    <Button 
                      type="button" 
                      variant="outline"
                      className="w-full bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100 hover:text-yellow-800" 
                      onClick={handleSetupMode}
                    >
                      🔧 Setup Mode
                    </Button>
                  )}
                </div>
              </form>

              {/* Sign Up Link */}
              <div className="text-center border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Punya kode registrasi dari administrator?
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/register')}
                >
                  Daftar dengan Kode Registrasi
                </Button>
              </div>
            </>
          )}
        </CardContent>
          </Card>

          <div className="mt-8 flex justify-center gap-6 text-xs font-medium text-slate-400">
            <span>PT. Gemilang Cipta Wawasan</span>
            <span aria-hidden="true">•</span>
            <span>Project Tracking System</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
